import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db, functions } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { reconnectFirestore } from './utils/firestoreReconnect';
import { perfMark } from './utils/perfMarks';
import { trace } from './utils/traceLog';
import { recordRoute } from './utils/routeTrail';
import { probeFirestoreRead, probeAuthToken } from './utils/wakeProbes';
import { withTransientRetry } from './utils/transientReadRetry';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import AuthForm from './components/AuthForm';
import NavBar from './components/NavBar';
import NativeTopSpacer from './components/NativeTopSpacer';
import { NotificationProvider } from './contexts/NotificationContext';
import { ContactProvider } from './contexts/ContactContext';
import { registerPushNotifications, setPushNavigator, setPushSession, unregisterPushNotifications } from './services/pushNotifications';
import SoftPushPromptModal from './components/SoftPushPromptModal';
import { useEdgeSwipeBack } from './hooks/useSwipeViews';
import { usePullToRefresh, PullToRefreshIndicator } from './hooks/usePullToRefresh';
import { usePullToRefreshContext } from './contexts/PullToRefreshContext';


import UserDashboard from './pages/UserDashboard';
// Dev-only: real-component pricing preview for the v2 pricing review
// (REVERB_PRICING_AUDIT_2026-10-08.md). Route mounts ONLY in development.
import PricingPreview from './pages/dev/PricingPreview';
import Pedalboard from './pages/Pedalboard';
import WantList from './pages/WantList';
import Matches from './pages/Matches';
import AddPedalPage from './pages/AddPedalPage';
import MatchDetails from './pages/MatchDetails';
import PedalDetails from './pages/PedalDetails';
import ArchivedListing from './pages/ArchivedListing';
import './App.css';
import SavedMatches from './pages/SavedMatches';
import HiddenMatches from './pages/HiddenMatches';
import OfferPage from './pages/OfferPage';
import TradeCenter from './pages/TradeCenter';
import OfferHistory from './pages/OfferHistory';
import AcceptedTrades from './pages/AcceptedTrades';
import AcceptedPurchases from './pages/AcceptedPurchases';
import TradeProgress from './pages/TradeProgress';
import TradeConfirm from './pages/TradeConfirm';
import TradeSettlement from './pages/TradeSettlement';
import SearchResults from './pages/SearchResults';
import CompletedTrades from './pages/CompletedTrades';
import CompletedPurchases from './pages/CompletedPurchases';
import MembershipSuccess from './pages/MembershipSuccess';
import GetStarted from './pages/GetStarted';
import HowItWorks from './pages/HowItWorks';
import TradeGuitarPedals from './pages/TradeGuitarPedals';
import Fees from './pages/Fees';
import DirectOffer from './pages/DirectOffer';
import InterestResponse from './pages/InterestResponse';
import PurchaseOffer from './pages/PurchaseOffer';
import PurchaseOfferDetails from './pages/PurchaseOfferDetails';
import PurchaseConfirm from './pages/PurchaseConfirm';
import SellerPurchaseConfirm from './pages/SellerPurchaseConfirm';
import SellerPurchaseSettlement from './pages/SellerPurchaseSettlement';
import PurchaseProgress from './pages/PurchaseProgress';
import LatestPedals from './pages/LatestPedals';
import NewsPage from './pages/NewsPage';
import { isPedalNewsUiEnabled, isGiveawayPromoEnabled } from './featureFlags';
import PublicProfile from './pages/PublicProfile';
import PublicPedalboard from './pages/PublicPedalboard';
import PublicWantList from './pages/PublicWantList';
import EmailPreview from './pages/EmailPreview';
import OnboardingStep5 from './pages/OnboardingStep5';
import OnboardingStep6 from './pages/OnboardingStep6';
import OnboardingStep7 from './pages/OnboardingStep7';
import TradeMessages from './pages/TradeMessages';
import TradingHub from './pages/TradingHub';
import MyPedalsHub from './pages/MyPedalsHub';
import Giveaway from './pages/Giveaway';
import EmailVerified from './pages/EmailVerified';
import CheckoutComplete from './pages/CheckoutComplete';
import DeleteAccount from './pages/DeleteAccount';
import BottomTabBar from './components/BottomTabBar';
import PageViewTracker from './components/PageViewTracker';

import { lazyRoute } from './components/LazyRoute';
// Route-level code splitting: these pages load as separate chunks on first
// visit (see src/components/LazyRoute.jsx for the loading/failure rail).
// Browse and the dashboard stay eagerly imported on purpose — they are the
// landing surfaces and must never wait on a second fetch.
const Profile = lazyRoute(() => import('./pages/Profile'));
const AdminDashboard = lazyRoute(() => import('./pages/AdminDashboard'));
const AdminEditListing = lazyRoute(() => import('./pages/AdminEditListing'));
const Policies = lazyRoute(() => import('./pages/Policies'));
const RecommendationsPage = lazyRoute(() => import('./pages/RecommendationsPage'));
const WantListMatchesPage = lazyRoute(() => import('./pages/WantListMatchesPage'));

/** Paths that should be indexed; all other app routes get noindex (see RouteRobotsHelmet). */
function isIndexablePathname(pathname) {
  const indexableExact = new Set([
    '/',
    '/dashboard',
    '/fees',
    '/how-it-works',
    '/get-started',
    '/latest-pedals',
    // '/news' left the list 2026-11-02 with the News gate (it redirects while off).
    '/policies',
    '/trade-guitar-pedals',
  ]);
  if (indexableExact.has(pathname)) return true;
  if (pathname.startsWith('/listing/')) return true;
  return false;
}

function RouteRobotsHelmet() {
  const { pathname } = useLocation();
  const indexable = isIndexablePathname(pathname);
  return (
    <Helmet>
      {!indexable && <meta name="robots" content="noindex, follow" />}
    </Helmet>
  );
}

// Routes that own a horizontal swipe gesture (tab/view switching) or already
// wire their own edge-swipe-back, so the global app-level swipe-back must NOT
// also fire there (it would double-handle / fight the page's own gesture):
//   - matches family + match-opportunity tabs (left/right swaps the tab)
//   - add pedal / add want (swaps between the two forms)
//   - my pedalboard / my wants (swaps between the two)
//   - listing detail (owns its own back swipe; tuned per page)
// Home/dashboard are excluded separately (nothing to navigate back to).
const SWIPE_BACK_OPT_OUT_PREFIXES = [
  '/matches',
  '/saved-matches',
  '/hidden-matches',
  '/recommendations',
  '/want-list-matches',
  '/addpedal',
  '/my-pedals',
  '/pedalboard', // bare route renders the same inline edit form (photo drag)
  '/listing/',
  '/trade-center', // bottom-tab root: iOS convention is tab roots don't swipe back. Its .tc-band-pedals strip is additionally marked data-swipe-back-opt-out.
  '/admin', // wide tables and a scrolling tab strip: every sideways scroll read as "go back" and threw the owner out of the console (2026-10-21).
  // '/offer-history' was opted out here 2026-09-29 because the .tc-band-pedals
  // strip fought the global swipe-back (the 1.0.7 class of bug). Removed
  // 2026-10-02: the strip now carries data-swipe-back-opt-out (element-level,
  // see startedInOptOut in useSwipeViews), so the page keeps its back-swipe
  // and only gestures born inside the strip are ignored — matching mobile web.
];

// Native-only wrapper for the global swipe-back. On web it renders children
// straight through (a Fragment adds no DOM node), so the web tree is byte-for-
// byte unchanged. On native it wraps the routed page so it can slide off-screen
// during the back animation and receives the react-swipeable handlers (ref).
function SwipeBackShell({ children, className, ...rest }) {
  if (!Capacitor.isNativePlatform()) return <>{children}</>;
  // minHeight fills the viewport so the back-swipe registers anywhere on screen
  // (not just over short page content). Transparent — the body background shows
  // through exactly as before, so nothing changes visually.
  return (
    <div className={className} style={{ minHeight: '100dvh' }} {...rest}>
      {children}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  // "Auth not yet known" vs "confirmed signed out" (src/utils/authResolution.js).
  // The dashboard renders neither experience until this is true, so a
  // signed-in load no longer paints the signed-out page first.
  const [authResolved, setAuthResolved] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Remember the previous route for pages that restore their position only
  // when the user returns straight from one of their own detail pages
  // (Offers, Offer History). See utils/routeTrail.js.
  useEffect(() => { recordRoute(location.pathname); }, [location.pathname]);
  const [searchParams] = useSearchParams();
  
  // Check for new nav testing flag
  const useNewNav = searchParams.get('newNav') === 'true';

  // Native push: let the tap handler route via React Router (app stays mounted).
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setPushNavigator(navigate);
    }
  }, [navigate]);

  // Native push: on login, SILENTLY register the FCM token if the user already
  // granted permission on a prior launch. We deliberately do NOT prompt here —
  // asking for notifications right after signup gets denied often and risks App
  // Store rejection. The OS prompt is instead deferred to a contextual moment
  // (after a meaningful action like listing a pedal, adding a want, or making an
  // offer) via the soft-ask flow (services/softPushPrompt + SoftPushPromptModal).
  // The push session follows the signed-in uid (login, logout from any
  // path, account switch) so registration work started for one account can
  // never store a token for another (services/pushNotifications.js).
  const uid = user?.uid;
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    setPushSession(uid || null);
    if (uid) registerPushNotifications({ promptIfNeeded: false });
  }, [uid]);

  // Native: iOS suspends the WebView while the app is backgrounded, which leaves
  // Firestore's connection stale on resume. Because native forces long-polling
  // (see firebase.js — experimentalForceLongPolling), that dead connection makes
  // the next write (updateDoc) hang forever: its promise never resolves. That's
  // why interactive writes like the offer "Send comment" button appear dead after
  // returning to the app (the button sits in its sending state and nothing posts).
  // Force Firestore to drop and rebuild its connection on every resume so the
  // next read/write goes through immediately. Native-only; web already recovers
  // via the App Check visibilitychange handler in firebase.js.
  // The toggle itself lives in utils/firestoreReconnect.js so this handler and
  // the OfferPage retry button share one coalesced cycle instead of
  // interleaving two independent disable/enable sequences.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let handle;
    const onResume = async () => {
      // Wake diagnostics (approved 2026-09-23): a one-doc Firestore read
      // probe and an auth-token probe before the cycle, and a second read
      // probe after it, so one real incident discriminates "transport dead
      // until cycled" from "auth token refresh stalled". Probes are
      // fire-and-forget and never block the reconnect or anything else;
      // they record only outcomes, codes, and durations (see wakeProbes.js).
      trace('wake', { reason: 'native-resume' });
      probeFirestoreRead('pre');
      probeAuthToken('pre');
      try {
        const result = await reconnectFirestore();
        if (result.outcome === 'blocked') {
          // A previous restart's SDK calls are still pending (2026-10-06):
          // reconnectFirestore requested nothing, so a resume can't stack a
          // second disable/enable pair behind a stuck one. Nothing to retry
          // here; the next resume, or a page's Try again, tries again once
          // the stuck call settles. (reconnectFirestore traces this itself.)
          console.warn('Firestore reconnect on resume skipped: an earlier reconnect is still pending');
        } else if (result.outcome !== 'recovered') {
          console.warn('Firestore reconnect on resume did not recover:', result.outcome);
        }
      } catch (err) {
        console.warn('Firestore reconnect on resume failed:', err);
      }
      probeFirestoreRead('post');
      // Pages with one-shot reads (dashboard Trade Opportunities) refresh on this.
      try { window.dispatchEvent(new Event('pxc:resumed')); } catch { /* no window */ }
    };
    CapacitorApp.addListener('resume', onResume).then((h) => {
      handle = h;
    });
    return () => {
      if (handle) handle.remove();
    };
  }, []);

  // Web wake diagnostics (2026-09-23 audit, item 5): a long-hidden tab has NO
  // Firestore recovery mechanism on web today, and the same stuck-loading
  // class reported on native also occurs there. These probes gather the
  // evidence that decides the fix. Deliberately NO network cycling here:
  // cycling the whole client on tab-return is deferred until a trace shows
  // the transport is what's dead (probe hangs pre-cycle, recovers post).
  // 5-minute threshold mirrors the App Check refresher in firebase.js.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      if (!hiddenAt) return;
      const hiddenSec = Math.round((Date.now() - hiddenAt) / 1000);
      hiddenAt = null;
      if (hiddenSec < 5 * 60) return;
      trace('wake', { reason: 'web-visible', hiddenSec });
      probeFirestoreRead('pre');
      probeAuthToken('pre');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Native shell background. The brand background is cream (#F7F3EB); auth pages
  // are white. On native the area outside the page content — the status-bar
  // strip painted by NativeTopSpacer, the bottom tab-bar padding band, and the
  // overscroll bounce — otherwise shows the default white <body>, which reads as
  // a stray white edge on cream pages (and a cream edge on white signup). Paint
  // <body> per route so those regions match the page. Native-only: env/web is
  // untouched (the effect early-returns on web).
  const nativeBg = (location.pathname === '/signup' || location.pathname === '/login')
    ? '#FFFFFF'
    : '#F7F3EB';
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = nativeBg;
    return () => { document.body.style.backgroundColor = prev; };
  }, [nativeBg]);

  // Global iOS-style swipe-to-go-back. The native shell hides the top NavBar on
  // most pages, removing the only back affordance, so a left-edge right-swipe
  // navigates back. Disabled on home/dashboard (no history target) and on pages
  // that own their own horizontal swipe (see SWIPE_BACK_OPT_OUT_PREFIXES) so we
  // don't fight their gesture. No-op on web (the hook returns {} there).
  const isHomeRoute = location.pathname === '/' || location.pathname === '/dashboard';
  const swipeBackEnabled = !isHomeRoute &&
    !SWIPE_BACK_OPT_OUT_PREFIXES.some(
      (p) => location.pathname === p || location.pathname.startsWith(p)
    );
  const { swipeProps: swipeBackProps, exitClass: swipeBackExitClass } = useEdgeSwipeBack(
    () => navigate(-1),
    // Match the Add Pedal/Want page feel (reliable everywhere): no edge gate,
    // delta 60, swipe from anywhere on the screen. 'enter' animation slides the
    // destination in from the left without sticking the persistent wrapper.
    { enabled: swipeBackEnabled, edgeGate: false, delta: 60, animation: 'enter' }
  );

  // Native pull-to-refresh: one gesture handler app-wide; each route may register
  // a data refresh via useRegisterPullToRefresh, otherwise we reload the page.
  const { runRefresh } = usePullToRefreshContext();
  const { showIndicator, pullDistance, refreshing: pullRefreshing } = usePullToRefresh(runRefresh);

  // Pull-to-refresh content motion: translate the page down by the pull distance
  // so it opens a gap (with the spinner) and holds while refreshing, then springs
  // back. We only apply the transform while pulling / settling — never at rest —
  // because a persistent `transform` would become the containing block for any
  // position:fixed children inside pages and break them.
  const [ptrSettling, setPtrSettling] = useState(false);
  const prevPullRef = useRef(0);
  useEffect(() => {
    if (prevPullRef.current > 0 && pullDistance === 0) {
      setPtrSettling(true);
      const t = setTimeout(() => setPtrSettling(false), 300);
      prevPullRef.current = 0;
      return () => clearTimeout(t);
    }
    prevPullRef.current = pullDistance;
    return undefined;
  }, [pullDistance]);

  const ptrDragging = pullDistance > 0 && !pullRefreshing;
  const ptrContentStyle = (pullDistance > 0 || ptrSettling)
    ? {
        transform: `translateY(${pullDistance}px)`,
        transition: ptrDragging ? 'none' : 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'transform',
      }
    : undefined;

  // Prevent right-click context menu on images (download prevention)
  // Allow right-click if the image is inside a link (preserves "Open in New Tab" on card views)
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' && !e.target.closest('a')) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Track website visit on page load
  useEffect(() => {
    const trackVisit = async () => {
      try {
        console.log('📊 Attempting to track website visit...');
        
        // Determine if we're on localhost or production
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const functionUrl = 'https://us-central1-pedalxchange.cloudfunctions.net/trackWebsiteVisit';
        
        let response;
        if (isLocalhost) {
          // On localhost, always use direct function call (rewrite won't work)
          console.log('🌐 Localhost detected, using direct function call');
          response = await fetch(functionUrl, {
            method: 'GET',
            mode: 'cors', // Use cors so we can see errors
            headers: {
              'Content-Type': 'application/json'
            }
          });
        } else {
          // On production, try rewrite first, then fallback
          try {
            response = await fetch('/api/track-visit', {
              method: 'GET',
              credentials: 'omit'
            });
          } catch (rewriteError) {
            console.warn('⚠️ Rewrite failed, trying direct function call:', rewriteError);
            response = await fetch(functionUrl, {
              method: 'GET',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json'
              }
            });
          }
        }
        
        if (response && response.ok) {
          const data = await response.json().catch(() => ({}));
          console.log('✅ Visit tracked successfully:', data);
        } else {
          const errorText = await response?.text().catch(() => 'Unknown error');
          console.error('❌ Visit tracking failed:', response?.status, errorText);
        }
      } catch (error) {
        // Log error for debugging
        console.error('❌ Visit tracking error:', error);
      }
    };

    // Track visit on initial load (debounce to avoid multiple calls)
    // Only track once per session to avoid inflating numbers
    // For testing, you can clear sessionStorage: sessionStorage.removeItem('visitTracked')
    const hasTracked = sessionStorage.getItem('visitTracked');
    if (!hasTracked) {
      const timeoutId = setTimeout(() => {
        trackVisit();
        sessionStorage.setItem('visitTracked', 'true');
      }, 1000);
      return () => clearTimeout(timeoutId);
    } else {
      console.log('ℹ️ Visit already tracked this session. Clear sessionStorage to test again.');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthResolved(true);
      perfMark('pxc:auth-resolved');
      
      // Track user activity for weekly active status (non-blocking)
      if (firebaseUser) {
        try {
          const captureIP = httpsCallable(functions, 'captureUserIP');
          captureIP({}).catch(error => {
            console.warn('⚠️ Failed to track user activity (non-critical):', error.message);
          });
        } catch (error) {
          console.warn('⚠️ Activity tracking unavailable:', error.message);
        }
      }
      
      // Check if user needs to complete signup
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          // One-shot read on the wake/offline-blip path (2026-09-29 incident):
          // when a transient blip makes this fail with "client is offline",
          // retry once (web only — on native the helper is a pass-through).
          // If both attempts fail the catch below logs and, as before, no
          // redirect happens.
          const userDoc = await withTransientRetry('signup-check', () => getDoc(userDocRef));

          // Stale-result barrier: if the signed-in uid changed while the read
          // (and possibly its retry) was in flight, this result describes the
          // WRONG account — deciding signup/onboarding redirects from it
          // could bounce the new user. The auth listener already fired again
          // for the new uid and will make its own fresh decision.
          if (auth.currentUser?.uid !== firebaseUser.uid) return;

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const isEmailVerified = userData.emailVerified || firebaseUser.emailVerified;
            const isPhoneVerified = userData.phoneVerified;
            const hasCompleteProfile = userData.profileComplete === true || (userData.username && userData.shippingAddress?.street1);
            const step6Complete = userData.step6Complete || false;
            const onboardingComplete = userData.onboardingComplete || false;
            
            // Check if user registered before new onboarding was implemented (Dec 3, 2025)
            // If so, consider them grandfathered in (onboarding complete)
            const onboardingLaunchDate = new Date('2025-12-03T00:00:00Z');
            const userCreatedAt = userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
            const isLegacyUser = userCreatedAt < onboardingLaunchDate;
            
            // Legacy users are automatically considered onboarding complete
            const effectiveOnboardingComplete = onboardingComplete || isLegacyUser;
            
            // Only redirect to signup for incomplete users on non-critical pages
            // This prevents interference with payment/trade flows while preserving signup flow
            const criticalPaths = [
              '/signup',
              '/onboarding-step-5',
              '/onboarding-step-6',
              '/onboarding-step-7',
              '/trade-progress/',
              '/purchase-progress/',
              '/trade-confirm',
              '/purchase-confirm',
              '/seller-purchase-confirm',
              '/seller-purchase-settlement',
              '/trade-settlement',
              '/purchase-offer-details',
              '/offer-details',
              '/membership-success',  // Stripe redirect for membership upgrades
              '/profile'  // Allow returning to profile after payments
            ];
            
            const isOnCriticalPath = criticalPaths.some(path => location.pathname.includes(path));
            
            // Redirect logic for incomplete registration/onboarding
            if (!isOnCriticalPath) {
              // Step 1-4: Check if basic profile is complete
              if (!hasCompleteProfile) {
                console.log('🔄 User signup incomplete, redirecting to signup');
                navigate('/signup');
              }
              // Step 5: Check if onboarding is complete
              // If onboardingComplete is true, user can navigate freely (Steps 6 & 7 are optional)
              else if (hasCompleteProfile && !effectiveOnboardingComplete) {
                // Redirect to Step 5 if they haven't completed it yet
                if (location.pathname !== '/onboarding-step-5') {
                  console.log('🔄 Onboarding Step 5 incomplete, redirecting to Step 5');
                  navigate('/onboarding-step-5');
                }
              }
              // Note: Steps 6 & 7 are now optional and do not trigger redirects
            }
          } else if (!userDoc.metadata.fromCache) {
            // No Firestore doc, CONFIRMED BY THE SERVER - very new user,
            // redirect to signup. A cache-served empty result (offline blip)
            // is deliberately NOT a redirect: sending a registered user to
            // /signup because the network hiccuped is the worse failure.
            if (location.pathname !== '/signup') {
              console.log('🔄 No user document, redirecting to signup');
              navigate('/signup');
            }
          }
        } catch (error) {
          console.error('Error checking user signup status:', error);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, location.pathname]);

  const handleLogout = async () => {
    try {
      await unregisterPushNotifications();
      await signOut(auth);
      navigate('/'); // or '/login' if that’s your login page
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
            <NotificationProvider>
            <ContactProvider>
            <div className="App">
        <SoftPushPromptModal />
        <PullToRefreshIndicator
          show={showIndicator}
          offset={pullDistance}
          refreshing={pullRefreshing}
        />
        <RouteRobotsHelmet />
        <PageViewTracker />
        {/* Pull-to-refresh translates this wrapper (navbar + page) down to open a
            gap with the spinner, then springs back. The bottom tab bar and spinner
            are siblings outside this wrapper so they stay put. */}
        <div style={ptrContentStyle}>
        {/* Native app only: hide the full top NavBar everywhere except the
            dashboard for a cleaner bottom-tab shell, and drop in a safe-area
            spacer that preserves status-bar clearance + the --navbar-height var.
            Web (and the dashboard on native) keep the normal NavBar. */}
        {location.pathname === '/checkout-complete' ? null :
          Capacitor.isNativePlatform() &&
          location.pathname !== '/' &&
          location.pathname !== '/dashboard' ? (
            <NativeTopSpacer background={nativeBg} />
          ) : (
            <NavBar />
          )}
        <SwipeBackShell {...swipeBackProps} className={swipeBackExitClass}>
        <Routes>
          {process.env.NODE_ENV === 'development' && (
            <Route path="/dev/pricing-preview" element={<PricingPreview />} />
          )}
          <Route path="/" element={<UserDashboard user={user} authResolved={authResolved} />} />
          <Route path="/dashboard" element={<UserDashboard user={user} authResolved={authResolved} />} />
          <Route
            path="/giveaway"
            element={
              isGiveawayPromoEnabled() ? <Giveaway /> : <Navigate to="/dashboard" replace />
            }
          />
          <Route path="/signup" element={<AuthForm isSignUp={true} />} />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="/checkout-complete" element={<CheckoutComplete />} />
          {/* Public deletion-instructions page — the URL Google Play's Data
              safety form requires. Must stay logged-out accessible. */}
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/login" element={<AuthForm />} />
          <Route path="/pedalboard" element={<Pedalboard />} />
          <Route path="/wantlist" element={<WantList />} />
          {/* Welcome and bi-weekly emails sent before 2026-10-18 linked to /want-list, which
              never existed as a route (blank page). Keep those links alive. */}
          <Route path="/want-list" element={<Navigate to="/wantlist" replace />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/addpedal" element={<AddPedalPage />} />
          <Route path="/match/:matchId" element={<MatchDetails />} />
          <Route path="/listing/:id" element={<PedalDetails />} />
          <Route path="/archived-listing/:type/:id/:pedalType/:pedalId" element={<ArchivedListing />} />
          <Route path="/archived-listing/:type/:id/:pedalType" element={<ArchivedListing />} />
          <Route path='/saved-matches' element={<SavedMatches />} />
          <Route path='/hidden-matches' element={<HiddenMatches />} />
          <Route path="/offer/:tradeId" element={<OfferPage />} />
          <Route path="/trade-center" element={<TradeCenter />} />
          <Route path="/offer-history" element={<OfferHistory />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:userId" element={<PublicProfile />} />
          <Route path="/pedalboard/:userId" element={<PublicPedalboard />} />
          <Route path="/wantlist/:userId" element={<PublicWantList />} />
          <Route path="/accepted-trades" element={<AcceptedTrades />} />
          <Route path="/accepted-purchases" element={<AcceptedPurchases />} />
          <Route path="/trade-progress/:id" element={<TradeProgress />} />
          <Route path="/trade-confirm/:id" element={<TradeConfirm />} />
          <Route path="/trade-settlement/:id" element={<TradeSettlement />} />
          <Route path="/trade-messages/:tradeId" element={<TradeMessages />} />
          <Route path="/purchase-messages/:tradeId" element={<TradeMessages />} />
          <Route path="/trading" element={<TradingHub />} />
          <Route path="/my-pedals/pedalboard" element={<MyPedalsHub />} />
          <Route path="/my-pedals/wants" element={<MyPedalsHub />} />
          <Route path="/my-pedals" element={<MyPedalsHub />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/latest-pedals" element={<LatestPedals />} />
          <Route
            path="/news"
            element={
              isPedalNewsUiEnabled() ? <NewsPage /> : <Navigate to="/dashboard" replace />
            }
          />
          <Route path="/recommendations" element={<RecommendationsPage />} />
          <Route path="/want-list-matches" element={<WantListMatchesPage />} />
          <Route path="/completed-trades" element={<CompletedTrades />} />
          <Route path="/completed-purchases" element={<CompletedPurchases />} />
          <Route path="/membership-success" element={<MembershipSuccess />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/trade-guitar-pedals" element={<TradeGuitarPedals />} />
          <Route path="/fees" element={<Fees />} />
          <Route path="/direct-offer" element={<DirectOffer />} />
          <Route path="/interest/:interestId" element={<InterestResponse />} />
          <Route path="/purchase-offer" element={<PurchaseOffer />} />
          <Route path="/purchase-offer-details/:offerId" element={<PurchaseOfferDetails />} />
          <Route path="/purchase-confirm/:offerId" element={<PurchaseConfirm />} />
          <Route path="/seller-purchase-confirm/:offerId" element={<SellerPurchaseConfirm />} />
          <Route path="/seller-purchase-settlement/:offerId" element={<SellerPurchaseSettlement />} />
          <Route path="/purchase-progress/:offerId" element={<PurchaseProgress />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/edit-listing/:listingId" element={<AdminEditListing />} />
          <Route path="/preview-emails" element={<EmailPreview />} />
          <Route path="/onboarding-step-5" element={<OnboardingStep5 />} />
          <Route path="/onboarding-step-6" element={<OnboardingStep6 />} />
          <Route path="/onboarding-step-7" element={<OnboardingStep7 />} />
        </Routes>
        </SwipeBackShell>
        </div>

        {/* Bottom Tab Bar - Mobile only (shown for all users). Hidden on the
            native checkout/onboarding handoff page so the in-app browser sheet
            shows a clean confirmation with no app chrome (navbar/login/tabs). */}
        {location.pathname !== '/checkout-complete' && <BottomTabBar />}
      </div>
        </ContactProvider>
        </NotificationProvider>
  );
}

export default App;
